'use client';

import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';

// Avance del proceso disciplinario para UN expediente. Guiado por datos reales:
// se calcula la fase a partir de `etapa_actual` (EtapaProcesal) y `estado_actual`
// (EstadoExpediente), tomando la más avanzada de ambas. No es un progreso simulado.

// Fases visibles del ciclo (agrupan las 18 EtapaProcesal / 24 EstadoExpediente reales).
// Exportado para que otras vistas (p. ej. el mapa de ruta del Inicio) usen la
// misma secuencia/etiquetas — el usuario debe reconocer el mismo mapa en ambos lados.
export const FASES = [
  'Radicación',
  'Reparto',
  'Instrucción',
  'Pliego y remisión',
  'Juzgamiento',
  'Fallo y recursos',
  'Segunda instancia',
  'Ejecución',
  'Archivo',
] as const;

// EtapaProcesal → índice de fase.
const ETAPA_FASE: Record<string, number> = {
  RADICACION: 0,
  REPARTO: 1,
  ANALISIS_INICIAL: 2,
  INDAGACION_PREVIA: 2,
  INVESTIGACION: 2,
  PRUEBAS: 2,
  CIERRE: 2,
  ALEGATOS_PRECALIFICATORIOS: 2,
  PLIEGO: 3,
  JUZGAMIENTO_ORDINARIO: 4,
  JUZGAMIENTO_VERBAL: 4,
  VARIACION_CARGOS: 4,
  ALEGATOS_CONCLUSION: 4,
  FALLO: 5,
  RECURSOS: 5,
  SEGUNDA_INSTANCIA: 6,
  EJECUCION: 7,
  ARCHIVO: 8,
};

// EstadoExpediente → índice de fase (más granular; suele ser el autoritativo).
const ESTADO_FASE: Record<string, number> = {
  PENDIENTE_REPARTO: 1,
  EN_ANALISIS_INICIAL: 2,
  AUTO_INHIBITORIO: 2,
  INDAGACION_PREVIA: 2,
  INVESTIGACION_DISCIPLINARIA: 2,
  PRACTICA_PRUEBAS: 2,
  CIERRE_INVESTIGACION: 2,
  ALEGATOS_PRECALIFICATORIOS: 2,
  PLIEGO_CARGOS: 3,
  PLIEGO_NOTIFICADO: 3,
  REMITIDO_JUZGAMIENTO: 4,
  PENDIENTE_REPARTO_JUZGAMIENTO: 4,
  JUICIO_ORDINARIO: 4,
  JUICIO_VERBAL: 4,
  VARIACION_CARGOS: 4,
  ALEGATOS_CONCLUSION: 4,
  FALLO_PRIMERA_INSTANCIA: 5,
  RECURSO_INTERPUESTO: 5,
  SEGUNDA_INSTANCIA: 6,
  FALLO_EJECUTORIADO: 7,
  EJECUCION_SANCION: 7,
  SANCION_CUMPLIDA: 8,
  ARCHIVO_DEFINITIVO: 8,
  ARCHIVO_DEFINITIVO_HISTORICO: 8,
};

// Convierte un valor de enum (MAYUS_CON_GUION) en texto legible.
function legible(v?: string | null): string {
  if (!v) return '—';
  const s = v.replace(/_/g, ' ').toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export default function AvanceProceso({
  etapaActual,
  estadoActual,
  className,
}: {
  etapaActual?: string | null;
  estadoActual?: string | null;
  className?: string;
}) {
  const idxEtapa = etapaActual ? ETAPA_FASE[etapaActual] ?? -1 : -1;
  const idxEstado = estadoActual ? ESTADO_FASE[estadoActual] ?? -1 : -1;
  // Fase actual = la más avanzada que reporten etapa y estado (mín. 0).
  const actual = Math.max(idxEtapa, idxEstado, 0);

  return (
    <div className={cn('rounded-lg border bg-card p-4', className)}>
      <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Avance del proceso · Etapa actual:{' '}
        <span className="text-foreground">{legible(etapaActual) }</span>
      </p>

      <ol className="flex items-start gap-0 overflow-x-auto pb-1">
        {FASES.map((fase, i) => {
          const estado = i < actual ? 'done' : i === actual ? 'actual' : 'pend';
          return (
            <li key={fase} className="flex min-w-[92px] flex-1 flex-col items-center">
              <div className="flex w-full items-center">
                {/* Conector izquierdo */}
                <span
                  className={cn(
                    'h-0.5 flex-1',
                    i === 0 ? 'opacity-0' : i <= actual ? 'bg-primary' : 'bg-border',
                  )}
                />
                {/* Círculo del paso */}
                <span
                  className={cn(
                    'flex size-8 shrink-0 items-center justify-center rounded-full border-2 text-xs font-semibold transition-colors',
                    estado === 'done' && 'border-primary bg-primary text-primary-foreground',
                    estado === 'actual' && 'border-primary bg-background text-primary ring-2 ring-primary/30',
                    estado === 'pend' && 'border-border bg-background text-muted-foreground',
                  )}
                >
                  {estado === 'done' ? <Check className="size-4" /> : i + 1}
                </span>
                {/* Conector derecho */}
                <span
                  className={cn(
                    'h-0.5 flex-1',
                    i === FASES.length - 1 ? 'opacity-0' : i < actual ? 'bg-primary' : 'bg-border',
                  )}
                />
              </div>
              <span
                className={cn(
                  'mt-1.5 text-center text-[11px] leading-tight',
                  estado === 'pend' ? 'text-muted-foreground' : 'font-medium text-foreground',
                )}
              >
                {fase}
              </span>
              <span
                className={cn(
                  'text-[10px]',
                  estado === 'actual' ? 'text-primary' : 'text-muted-foreground/70',
                )}
              >
                {estado === 'done' ? 'Completado' : estado === 'actual' ? 'En curso' : 'Pendiente'}
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
